import {
  type SerializedCompStructure,
  type SerializedPageStructure,
} from '@wix/document-services-types'
import { entries } from '@wix/wix-data-client-common-standalone'

import { type DataBindingControllerConnection } from '../types'
import {
  applyModifiers,
  assertDataBindingController,
  assertNonNullable,
  assertStackItemLayout,
  copyComponent,
  generateConnectionConfigs,
  getComponentByVeloId,
  modifyComponentByVeloId,
  setControllerConnectionConfigs,
  setControllerConnectionsToComponent,
} from '../utils'
import { ROUTER_DATASET_VELO_ID } from '../veloIds'
import { adjustComponent } from './adjustComponent'
import { type ComponentAdjustments } from './types'
import { FIELD_STACK_VELO_ID } from './veloIds'

export const addComponentForField = ({
  page,
  templateComponentByVeloId,
  templateControllerConnectionsByVeloId,
  templateVeloId,
  componentAdjustments,
  subComponentAdjustmentsByVeloId,
}: {
  page: SerializedPageStructure
  templateComponentByVeloId: { [VeloId: string]: SerializedCompStructure }
  templateControllerConnectionsByVeloId: {
    [VeloId: string]: DataBindingControllerConnection[]
  }
  templateVeloId: string
  componentAdjustments: ComponentAdjustments
  subComponentAdjustmentsByVeloId?: { [VeloId: string]: ComponentAdjustments }
}): SerializedPageStructure => {
  const connectionConfigByRoleByVeloId = generateConnectionConfigs({
    connectionConfigModifierByRoleByVeloId: Object.fromEntries(
      entries({
        [templateVeloId]: componentAdjustments,
        ...subComponentAdjustmentsByVeloId,
      })
        .filter(
          (
            entry,
          ): entry is [
            string,
            ComponentAdjustments & {
              connectionConfigModifierByRole: Exclude<
                ComponentAdjustments['connectionConfigModifierByRole'],
                undefined
              >
            },
          ] => entry[1].connectionConfigModifierByRole !== undefined,
        )
        .map(([veloId, { connectionConfigModifierByRole }]) => [
          veloId,
          connectionConfigModifierByRole,
        ]),
    ),
    controllerConnectionsByVeloId: templateControllerConnectionsByVeloId,
  })

  const fieldStack = getComponentByVeloId({
    root: page,
    veloId: FIELD_STACK_VELO_ID,
  })
  assertNonNullable(fieldStack.components)
  const order = fieldStack.components.length + 1

  const componentForField = adjustComponent({
    component: copyComponent({
      component: templateComponentByVeloId[templateVeloId],
      uniquifyIdOrSig: idOrSig => `${idOrSig}-${order}`,
    }),

    modifyComponent: component =>
      applyModifiers({
        target: component,
        modifiers: {
          modifyComponent: componentAdjustments.modifyComponent,

          setOrder: component => {
            assertNonNullable(component.layouts?.itemLayout)
            assertStackItemLayout(component.layouts.itemLayout)
            return {
              ...component,
              layouts: {
                ...component.layouts,
                itemLayout: { ...component.layouts.itemLayout, order },
              },
            }
          },

          adjustSubComponents:
            subComponentAdjustmentsByVeloId !== undefined
              ? component =>
                  entries(subComponentAdjustmentsByVeloId).reduce(
                    (component, [veloId, subComponentAdjustments]) =>
                      modifyComponentByVeloId({
                        root: component,
                        veloId,
                        modifyComponent: subComponent =>
                          adjustComponent({
                            component: subComponent,
                            modifyComponent:
                              subComponentAdjustments.modifyComponent,
                            connectionConfigByRole:
                              connectionConfigByRoleByVeloId[veloId],
                            veloId: subComponentAdjustments.veloId,
                          }),
                      }),
                    component,
                  )
              : undefined,
        },
      }),
    connectionConfigByRole: connectionConfigByRoleByVeloId[templateVeloId],
    veloId: componentAdjustments.veloId,
  })

  return applyModifiers({
    target: page,
    modifiers: {
      addComponentForField: page =>
        modifyComponentByVeloId({
          root: page,
          veloId: FIELD_STACK_VELO_ID,
          modifyComponent: fieldStack => {
            assertNonNullable(fieldStack.components)
            return {
              ...fieldStack,
              components: [...fieldStack.components, componentForField],
            }
          },
        }),

      addControllerConnections: page =>
        modifyComponentByVeloId({
          root: page,
          veloId: ROUTER_DATASET_VELO_ID,
          modifyComponent: routerDataset => {
            assertDataBindingController(routerDataset)
            return entries(connectionConfigByRoleByVeloId).reduce(
              (controller, [veloId, configByRole]) => {
                const component = (() => {
                  if (veloId === templateVeloId) {
                    return componentForField
                  }

                  assertNonNullable(subComponentAdjustmentsByVeloId)
                  assertNonNullable(subComponentAdjustmentsByVeloId[veloId])
                  const { veloId: subComponentVeloId } =
                    subComponentAdjustmentsByVeloId[veloId]

                  return getComponentByVeloId({
                    root: componentForField,
                    veloId: subComponentVeloId,
                  })
                })()
                assertNonNullable(component.id)

                return setControllerConnectionsToComponent({
                  controller,
                  componentId: component.id,
                  connections: setControllerConnectionConfigs({
                    connections: templateControllerConnectionsByVeloId[veloId],
                    configByRole,
                  }),
                })
              },
              routerDataset,
            )
          },
        }),
    },
  })
}
